import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface ProgressStep {
  number: number;
  title: string;
  optional?: boolean;
}

@Component({
  selector: 'app-progress-steps',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './progress-steps.component.html',
  styleUrls: ['./progress-steps.component.css']
})
export class ProgressStepsComponent implements OnInit, OnChanges {
  @Input() steps: ProgressStep[] = [];
  @Input() currentStep: number = 1;
  @Input() canNavigate: boolean = true;
  @Input() hasError: boolean = false;
  @Input() errorStep?: number; // Step number that has error (submitted with errors)
  @Input() warningSteps: number[] = []; // Steps with validation warnings (invalid but not submitted)
  @Input() disabledSteps: number[] = []; // Steps that cannot be navigated to
  @Input() submitted: boolean = false; // If form was submitted
  
  @Output() stepChange = new EventEmitter<number>();
  @Output() stepClick = new EventEmitter<number>();

  private previousStep: number = 1;

  ngOnInit(): void {
    this.previousStep = this.currentStep;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentStep'] && !changes['currentStep'].firstChange) {
      this.previousStep = changes['currentStep'].previousValue || 1;
    }
  }

  /**
   * Verifica se pode navegar para uma etapa
   */
  canNavigateToStep(stepNumber: number): boolean {
    if (!this.canNavigate) return false;
    if (this.disabledSteps.includes(stepNumber)) return false;
    return true;
  }

  /**
   * Obtém classes CSS para o círculo da etapa
   */
  getStepClasses(stepNumber: number): string {
    const isCurrent = stepNumber === this.currentStep;
    const isCompleted = stepNumber < this.currentStep;
    const hasError = this.submitted && this.hasError && this.errorStep === stepNumber;
    const hasWarning = !this.submitted && this.warningSteps.includes(stepNumber);
    const isDisabled = !this.canNavigateToStep(stepNumber);

    // Error state (submitted with errors) - vermelho
    if (hasError) {
      return 'bg-red-500 text-white border-2 border-red-600 shadow-lg shadow-red-200';
    }

    // Warning state (invalid but not submitted) - amarelo
    if (hasWarning) {
      return 'bg-yellow-500 text-white border-2 border-yellow-600 shadow-lg shadow-yellow-200';
    }

    // Current step - azul
    if (isCurrent) {
      return 'bg-[#4A90E2] text-white border-2 border-[#4A90E2] shadow-lg shadow-blue-200';
    }

    // Completed step - azul
    if (isCompleted) {
      return 'bg-[#4A90E2] text-white border-2 border-[#4A90E2]';
    }

    // Disabled step - cinza
    if (isDisabled) {
      return 'bg-gray-200 text-gray-400 border-2 border-gray-300';
    }

    // Pending step - cinza
    return 'bg-gray-200 text-gray-500 border-2 border-gray-300';
  }

  /**
   * Obtém classes CSS para o texto da etapa
   */
  getStepTextClasses(stepNumber: number): string {
    const isCurrent = stepNumber === this.currentStep;
    const isCompleted = stepNumber < this.currentStep;
    const hasError = this.submitted && this.hasError && this.errorStep === stepNumber;
    const hasWarning = !this.submitted && this.warningSteps.includes(stepNumber);
    const isDisabled = !this.canNavigateToStep(stepNumber);

    if (hasError) {
      return 'text-red-600 font-semibold';
    }

    if (hasWarning) {
      return 'text-yellow-600 font-semibold';
    }

    if (isCurrent) {
      return 'text-gray-900 font-semibold';
    }

    if (isCompleted) {
      return 'text-gray-700';
    }

    if (isDisabled) {
      return 'text-gray-400';
    }

    return 'text-gray-500';
  }

  /**
   * Obtém classes CSS para a barra de progresso
   */
  getProgressBarClasses(stepNumber: number): string {
    const isCompleted = stepNumber < this.currentStep;
    
    if (isCompleted) {
      return 'bg-[#4A90E2]';
    }

    return 'bg-gray-200';
  }

  /**
   * Obtém classes CSS para animação de transição
   */
  getTransitionClasses(stepNumber: number): string {
    const isCurrent = stepNumber === this.currentStep;
    const isPrevious = stepNumber === this.previousStep;
    
    if (isCurrent || isPrevious) {
      return 'transition-all duration-300 ease-in-out';
    }

    return '';
  }

  /**
   * Handler para clique na etapa
   */
  onStepClick(stepNumber: number): void {
    if (this.canNavigateToStep(stepNumber)) {
      this.stepClick.emit(stepNumber);
      if (stepNumber !== this.currentStep) {
        this.stepChange.emit(stepNumber);
      }
    }
  }

  /**
   * Verifica se a etapa tem warning (não submetido)
   */
  hasWarning(stepNumber: number): boolean {
    return !this.submitted && this.warningSteps.includes(stepNumber);
  }

  /**
   * Verifica se a etapa tem erro (submetido)
   */
  hasErrorState(stepNumber: number): boolean {
    return this.submitted && this.hasError && this.errorStep === stepNumber;
  }

  /**
   * Obtém tooltip para a etapa
   */
  getStepTooltip(stepNumber: number): string {
    if (!this.canNavigateToStep(stepNumber)) {
      return 'Não é possível navegar para esta etapa';
    }
    
    if (this.hasErrorState(stepNumber)) {
      return 'Esta etapa possui erros de validação';
    }

    if (this.hasWarning(stepNumber)) {
      return 'Esta etapa possui campos inválidos ou obrigatórios não preenchidos';
    }

    if (stepNumber === this.currentStep) {
      return 'Etapa atual';
    }

    if (stepNumber < this.currentStep) {
      return 'Etapa concluída - Clique para revisar';
    }

    return 'Clique para ir para esta etapa';
  }
}
